// Events of d2c and accounting [02 §5.1; 07 §15 BR-D2C-004/010, §10.2 P27/P29]. D2C payloads carry
// no consumer personal data (07 BR-D2C-005 masking; MB D18).
import { z } from 'zod';

import { businessDateSchema, idSchema } from '../kernel/index.ts';
import { moneySchema, qtyDecimalSchema } from '../units/index.ts';
import { utcTimestampSchema } from '../dto/scalars.ts';
import { LOCK_MODULE } from '../enums/index.ts';
import { defineEvent } from './define.ts';

export const d2cOrderCapturedV1 = defineEvent({
  type: 'd2c.order.captured',
  version: 1,
  aggregateType: 'd2c_order',
  specRefs: ['02 §5.1'],
  payload: z.object({
    d2cOrderId: idSchema,
    shopifyOrderGid: z.string().min(1),
    capturedAt: utcTimestampSchema,
    total: moneySchema,
    lines: z
      .array(
        z.object({
          shopifyVariantGid: z.string().min(1),
          itemId: idSchema.nullable(),
          qty: qtyDecimalSchema,
        }),
      )
      .min(1),
  }),
});

/** P29 D2C payout: Dr BANK (net), PAYMENT_FEES; Cr SHOPIFY_CLEARING. */
export const d2cPayoutReceivedV1 = defineEvent({
  type: 'd2c.payout.received',
  version: 1,
  aggregateType: 'd2c_payout',
  specRefs: ['02 §5.1', '07 §10.2 P29'],
  payload: z.object({
    d2cPayoutId: idSchema,
    shopifyPayoutId: z.string().min(1),
    payoutDate: businessDateSchema,
    gross: moneySchema,
    fees: moneySchema,
    adjustments: moneySchema,
    net: moneySchema,
  }),
});

/**
 * P27 daily summary journal (BR-D2C-004). Accounting may not import d2c, so the closed
 * `d2c_daily_summary` reaches posting through this event [assumption: event name].
 */
export const d2cDailySummaryReadyV1 = defineEvent({
  type: 'd2c.daily_summary.ready',
  version: 1,
  aggregateType: 'd2c_daily_summary',
  specRefs: ['07 §10.2 P27 [assumption: event name]'],
  payload: z.object({
    d2cDailySummaryId: idSchema,
    businessDate: businessDateSchema,
    sales: moneySchema,
    discounts: moneySchema,
    shipping: moneySchema,
    refunds: moneySchema,
    taxes: z.array(z.object({ taxCode: z.string().min(1), amount: moneySchema })),
  }),
});

export const accountingPeriodClosedV1 = defineEvent({
  type: 'accounting.period.closed',
  version: 1,
  aggregateType: 'fiscal_period',
  specRefs: ['02 §5.1'],
  payload: z.object({
    periodId: idSchema,
    fiscalYear: z.int(),
    period: z.int().min(0).max(13),
    module: LOCK_MODULE.schema,
    closedAt: utcTimestampSchema,
  }),
});

/** Longest AR credit reason key (`cancellation`); the reason list itself belongs to the AR context. */
const AR_CREDIT_REASON_MAX = 30;

/**
 * CR-BG2c (W2c.ar-bank R-EVT-AR-1): a credit note was issued (BR-AR-011/012). The returns flow closes the customer
 * return; orders restore the open quantity when `returnsToOrder` (BR-AR-012). No amounts: consumers read the document.
 */
export const accountingArCreditIssuedV1 = defineEvent({
  type: 'accounting.ar_credit.issued',
  version: 1,
  aggregateType: 'ar_document',
  specRefs: ['07 BR-AR-011', '07 BR-AR-012'],
  payload: z.object({
    creditNoteId: idSchema,
    reason: z.string().regex(/^[a-z][a-z_]*$/).max(AR_CREDIT_REASON_MAX),
    customerReturnId: idSchema.nullable(),
    returnsToOrder: z.boolean(),
  }),
});

/** Longest Shopify webhook topic, as `channels.webhook_event.topic` (varchar 60). */
const WEBHOOK_TOPIC_MAX = 60;

/**
 * CR-BG2f (W2f.d2c-orders R-CORE-D2C-5): a verified Shopify webhook was stored (`channels.webhook_event`, deduped on
 * `X-Shopify-Webhook-Id`) in the same transaction as this event: store-then-process through the outbox; the sweep job
 * stays as the backstop. Ids and the topic only — the consumer re-reads the stored payload (no consumer data here).
 */
export const d2cWebhookReceivedV1 = defineEvent({
  type: 'd2c.webhook.received',
  version: 1,
  aggregateType: 'webhook_event',
  specRefs: ['07 §15.2 #4', '07 BR-D2C-012'],
  payload: z.object({
    webhookEventId: idSchema,
    storeId: idSchema,
    topic: z
      .string()
      .max(WEBHOOK_TOPIC_MAX)
      .regex(/^[a-z][a-z0-9_]*(\/[a-z][a-z0-9_]*)+$/),
  }),
});

export const CHANNELS_EVENTS = [
  d2cOrderCapturedV1,
  d2cPayoutReceivedV1,
  d2cDailySummaryReadyV1,
  accountingPeriodClosedV1,
  accountingArCreditIssuedV1,
  d2cWebhookReceivedV1,
] as const;
